/**
 * The headless heart of the 3D dollhouse view: camera, scene, render
 * orchestration, and picking — everything View3D does that is not DOM.
 *
 * A platform shell (web canvas, expo-gl) owns the render surface, the input
 * events, and the label overlay. It forwards input as rotate/pan/zoom/pick
 * calls and gadget drags (see gadget.ts), calls `advance` each frame so a
 * camera glide moves, and drives a render loop off the `dirty` flag; each draw() renders the
 * GL frame, the drafting grid behind the scene included (gridRenderer.ts),
 * and returns the overlay placements (label screen positions) for the shell
 * to apply to its native overlay layer.
 *
 * Interaction (per the product spec): drag and scroll perform the shell's
 * selected tool — rotate (grabbing the tip of the out-of-screen vector) or pan
 * (sliding the plan under the camera); pinch zooms.
 * Each of the four primary walls (and everything on it) is shown or hidden as
 * a unit, based on whether its room-facing normal points out of the screen —
 * so you can look into the room from any angle.
 *
 * The shell says once, at construction, whether a mouse or a finger drives
 * it: under touch the move gadget is touch-sized and a tap reaches a touch
 * target past a scene object's edges (see gadget.ts and pick3d.ts).
 *
 * It draws at the chosen `View3DQuality` (see view3DQuality.ts): High through
 * the `LitRenderer`, Low through the `GLRenderer`, both shaded by the one
 * scene light. The view cube (viewCube.ts) turns the camera through
 * `viewFrom`, `goHome`, `turnView` and `setCameraAngle`, and each frame's
 * `angle` turns the cube with it.
 */

import { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { type InputKind, Selection, TOUCH_TARGET_REACH } from '../editor-model';
import { add, dot3d, Mat4, scale, sub, Vec3 } from '../math3d';
import { buildScene3D, type Scene3D, type Scene3DOptions, type SceneLabel } from './buildScene3d';
import { Camera, type PanReach } from './camera3d';
import {
  beginGadgetDrag,
  buildGadget,
  type GadgetDelta,
  type GadgetDrag,
  gadgetDragDelta,
  type GadgetHandle,
  type GadgetLayout,
  pickGadget,
  type SceneGadget,
} from './gadget';
import { GLRenderer } from './glRenderer';
import { GridRenderer } from './gridRenderer';
import { LitRenderer } from './litRenderer';
import { type Pickable3D, pickAt, type PickRay, type PickReach } from './pick3d';
import { DEFAULT_VIEW3D_QUALITY, type View3DQuality } from './view3DQuality';

/** Screen placement for one scene label (indexed like `labels`), in the same
 *  (CSS-pixel) units the shell passed to draw(). */
export interface LabelPlacement {
  x: number;
  y: number;
  /** False when the label's anchor is behind the camera. */
  visible: boolean;
}

/**
 * Per-frame placement of the drafting-paper grid the view draws behind its
 * scene, mirroring the 2D view: 1-ft minor / 5-ft major lines, one cell = one
 * foot on the plane through the camera target (≈ the room center), anchored
 * at the target's projected screen point so the grid tracks the room rather
 * than the viewport.
 */
export interface GridPlacement {
  majorPixels: number;
  /** Zoomed way out, 1-ft lines would dominate: minors collapse onto the
   *  majors (equal spacing = invisible) below ~5px, like fading detail on paper. */
  minorPixels: number;
  anchorX: number;
  anchorY: number;
}

/** What a shell needs to place its overlays after a draw. */
export interface OverlayFrame {
  labels: LabelPlacement[];
  /** The grid this frame drew behind the scene, for a shell still drawing a grid of its own. */
  grid: GridPlacement;
  /** Which way the camera looked for this frame: the view cube turns to it (`viewCubeLayout`). */
  angle: CameraAngle;
}

/**
 * Snapshot of the orbit camera. Exists so a shell that must REBUILD its GL
 * surface (expo-gl never resizes a framebuffer, so RN remounts the GLView on
 * layout changes) can hand the user's rotation and zoom to the replacement
 * core instead of resetting the view. Aspect-independent — the fit distance
 * comes from the vertical FOV and scene radius alone — so it carries verbatim
 * across a resize; the new surface supplies the new aspect at draw time.
 */
export interface CameraState {
  target: Vec3;
  azimuth: number;
  elevation: number;
  distance: number;
}

/** Which way a camera looks: its turn about the vertical and its height above the plan, in radians. */
export type CameraAngle = Pick<CameraState, 'azimuth' | 'elevation'>;

/** Which way `turnView` turns the house. */
export type TurnDirection = 'left' | 'right';

/** How far (px) a press may travel and still be a tap that picks: further, it is a rotate, a pan, or a gadget drag. Every shell holds its taps to it. */
export const TAP_MAX_DISTANCE_PX = 5;

/**
 * Where the opening view of `scene` looks from: its target, the middle of the
 * plan at a third of the ceiling up, the radius that fits the whole model,
 * and how far a pan may carry it (the plan's own width left or right and its
 * own depth forward or back, so three times the plan each way).
 */
export function sceneHome<S>(scene: Scene3D<S>): { center: Vec3; radius: number; reach: PanReach } {
  const { bbox: boundingBox } = scene;
  return {
    center: [
      (boundingBox.minX + boundingBox.maxX) / 2 - scene.center.x,
      scene.ceiling * 0.35,
      (boundingBox.minY + boundingBox.maxY) / 2 - scene.center.y,
    ],
    radius: 0.5 * Math.hypot(boundingBox.maxX - boundingBox.minX, boundingBox.maxY - boundingBox.minY) + scene.ceiling * 0.5,
    reach: { x: boundingBox.maxX - boundingBox.minX, z: boundingBox.maxY - boundingBox.minY },
  };
}

/**
 * `S` is the selection type a pick resolves to: the editor's `Selection` when
 * the scene is one room (`setShape`), or whatever a composed scene's
 * pickables carry (`setScene` — the assembler selects whole rooms).
 */
export class View3DCore<S = Selection> {
  private renderer: GLRenderer;
  private lit: LitRenderer | null = null;
  private grid: GridRenderer;
  private quality: View3DQuality = DEFAULT_VIEW3D_QUALITY;
  private camera = new Camera();
  private wallNormals: Vec3[] = [];
  private wallPoints: Vec3[] = [];
  private sceneLabels: SceneLabel[] = [];
  private pickables: Pickable3D<S>[] = [];
  /** The move gadget: its anchor from the scene, the layout of the last frame (for picking), and the drag in progress. */
  private gadget: SceneGadget | null = null;
  private gadgetLayout: GadgetLayout | null = null;
  private gadgetDrag: GadgetDrag | null = null;
  private framed = false;
  private openingAngle: CameraAngle | null = null;
  private readonly inputKind: InputKind;
  /** Set by every mutation; cleared by draw(). The shell's render loop
   *  redraws when it sees this true (and sets it after surface resizes). */
  dirty = true;

  constructor(gl: WebGLRenderingContext, inputKind: InputKind = 'pointer') {
    this.inputKind = inputKind;
    this.renderer = new GLRenderer(gl);
    this.grid = new GridRenderer(gl);
    const share = this.renderer.litShare();
    if (share) this.lit = new LitRenderer(share.gl, share.vbo, share.uintIndex, share.getSurfaces);
  }

  /** Whether High quality can draw here (WebGL2 float color buffers); where it cannot, High draws Low. */
  get litSupported(): boolean {
    return !!this.lit?.supported;
  }

  /** Draw at `quality` from the next frame on; High where it cannot draw draws Low. */
  setQuality(quality: View3DQuality): void {
    this.quality = quality;
    this.dirty = true;
  }

  /** The scene's labels; placements come back from each draw(), aligned by
   *  index. Changes with each setShape or setScene. */
  get labels(): readonly SceneLabel[] {
    return this.sceneLabels;
  }

  /** Show one room: the editor's scene, built here from the shape; `gadget` false leaves the move gadget out (a read-only host). */
  setShape(this: View3DCore<Selection>, shape: FloorPlanRoom, selection?: Selection, gadget = true, options?: Scene3DOptions): void {
    const scene = buildScene3D(shape, selection, options);
    this.setScene(gadget ? scene : { ...scene, gadget: undefined });
  }

  /**
   * Show an already-built scene (a room's, or the assembler's composition of
   * many rooms). The camera frames it on the first scene only — or whenever
   * `reframe` is set — so orbit/zoom survive ordinary scene updates.
   */
  setScene(scene: Scene3D<S>, reframe = !this.framed): void {
    this.renderer.setMesh(scene.mesh);
    this.wallNormals = scene.wallNormals;
    this.wallPoints = scene.wallPoints;
    this.sceneLabels = scene.labels;
    this.pickables = scene.pickables;
    this.gadget = scene.gadget ?? null;
    const { center, radius, reach } = sceneHome(scene);
    if (reframe) {
      this.camera.frame(center, radius, reach);
      this.lookFromOpeningAngle();
      this.framed = true;
    } else {
      this.camera.setHome(center, radius, reach);
    }
    this.dirty = true;
  }

  /** Glide back to the opening view: the home target and distance at the 3/4 angle, eased like `centerOn`; `advance` moves it. */
  goHome(): void {
    this.camera.glideHome();
    this.dirty = true;
  }

  /**
   * Glide the camera to look from `angle` (a view cube region's
   * `viewCubeAngle`), the nearest way around, eased like `centerOn`; it keeps
   * its target and distance, and `advance` moves it. Its elevation reaches
   * the view cube's Top and Bottom, past where a rotate drag goes.
   */
  viewFrom(angle: CameraAngle): void {
    this.camera.glideToAngle(angle);
    this.dirty = true;
  }

  /**
   * Glide a quarter turn about the vertical, as a drone circling the house
   * would see it: the house turns `direction`, the way a view cube drag that
   * direction turns it, eased like `centerOn`. The elevation, target and
   * distance stay. A turn tapped while one glides adds to where that one
   * would end, so taps in quick succession add up.
   */
  turnView(direction: TurnDirection): void {
    this.camera.glideTurn(direction === 'right' ? -1 : 1);
    this.dirty = true;
  }

  /** Look from `angle` at once (a view cube drag), within the view cube's Top and Bottom; a glide's turn stops. */
  setCameraAngle(angle: CameraAngle): void {
    this.camera.setAngle(angle);
    this.dirty = true;
  }

  /** The camera snapshot to carry across a GL surface rebuild — see CameraState. */
  getCameraState(): CameraState {
    return {
      target: [...this.camera.target],
      azimuth: this.camera.azimuth,
      elevation: this.camera.elevation,
      distance: this.camera.distance,
    };
  }

  /** Look from `angle` when the first scene is framed, in place of the 3/4 opening angle, its elevation within the view cube's Top and Bottom. Home keeps the 3/4 angle. */
  openAt(angle: CameraAngle): void {
    this.openingAngle = angle;
  }

  private lookFromOpeningAngle(): void {
    if (!this.openingAngle) return;
    this.camera.setAngle(this.openingAngle);
    this.openingAngle = null;
  }

  /** Restore a carried camera. Counts as framed: the next setShape must keep it, not re-frame. */
  setCameraState(state: CameraState): void {
    this.camera.target = [...state.target];
    this.camera.azimuth = state.azimuth;
    this.camera.elevation = state.elevation;
    this.camera.distance = state.distance;
    this.framed = true;
    this.dirty = true;
  }

  /** Rotation from a drag/scroll of (deltaX, deltaY) screen px (see Camera.rotate). */
  rotate(deltaXPixels: number, deltaYPixels: number): void {
    this.camera.rotate(deltaXPixels, deltaYPixels);
    this.dirty = true;
  }

  /** Raise the camera by `upPixels` measured at its target on a view `viewHeight` CSS px tall; negative lowers it (see Camera.lift). */
  lift(upPixels: number, viewHeight: number): void {
    this.camera.lift(upPixels / this.camera.pixelsPerFoot(viewHeight));
    this.dirty = true;
  }

  zoom(factor: number): void {
    this.camera.zoom(factor);
    this.dirty = true;
  }

  /** Glide the camera so `point` ends at the center of the view (see Camera.glideTo); `advance` moves it. */
  centerOn(point: Vec3): void {
    this.camera.glideTo(point);
    this.dirty = true;
  }

  /** Move a camera glide on to `nowMs`; shells call it once per animation frame, before checking `dirty`. */
  advance(nowMs: number): void {
    if (this.camera.advance(nowMs)) this.dirty = true;
  }

  /**
   * Pan from a drag/scroll of (deltaX, deltaY) screen px over a view of the
   * given CSS-pixel size: the plan slides with the pointer — see Camera.pan.
   */
  pan(deltaXPixels: number, deltaYPixels: number, viewWidth: number, viewHeight: number): void {
    this.camera.pan(deltaXPixels, deltaYPixels, viewWidth, viewHeight);
    this.dirty = true;
  }

  /** A world point projected to view coordinates, or null behind the camera. */
  private projectToView(
    viewProjection: Mat4,
    [x, y, z]: Vec3,
    viewWidth: number,
    viewHeight: number,
  ): { x: number; y: number } | null {
    const matrix = viewProjection;
    const w = matrix[3] * x + matrix[7] * y + matrix[11] * z + matrix[15];
    if (w <= 1e-6) return null;
    return {
      x: ((matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12]) / w * 0.5 + 0.5) * viewWidth,
      y: (0.5 - (matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13]) / w * 0.5) * viewHeight,
    };
  }

  private gridPlacement(viewProjection: Mat4, viewWidth: number, viewHeight: number): GridPlacement {
    const minorPixels = this.camera.pixelsPerFoot(viewHeight);
    const majorPixels = minorPixels * 5;
    const anchor = this.projectToView(viewProjection, this.camera.target, viewWidth, viewHeight)
      ?? { x: viewWidth / 2, y: viewHeight / 2 };
    return {
      majorPixels,
      minorPixels: minorPixels >= 5 ? minorPixels : majorPixels,
      anchorX: anchor.x,
      anchorY: anchor.y,
    };
  }

  private labelPlacements(viewProjection: Mat4, viewWidth: number, viewHeight: number): LabelPlacement[] {
    return this.sceneLabels.map(sceneLabel => {
      const projected = this.projectToView(viewProjection, sceneLabel.pos, viewWidth, viewHeight);
      return projected ? { x: projected.x, y: projected.y, visible: true } : { x: 0, y: 0, visible: false };
    });
  }

  /** Is primary wall group `wallIndex` currently facing the camera? Perspective-correct
   *  per-wall facing test: a wall's interior shows when its room-facing normal
   *  points toward the eye *from that wall* — the same test a GPU uses for
   *  back-faces. Because each wall is sampled at its own point, the live
   *  perspective can reveal three of the four interior walls at once. */
  private wallVisible(eye: Vec3, wallIndex: number): boolean {
    return dot3d(this.wallNormals[wallIndex], sub(eye, this.wallPoints[wallIndex])) > 0;
  }

  /**
   * Render one frame and clear `dirty`. (devicePixelWidth, devicePixelHeight)
   * is the surface size in device pixels; (viewWidth, viewHeight) is the view
   * size in the shell's overlay units (CSS px on web) — the returned
   * placements are in those units.
   */
  draw(
    devicePixelWidth: number,
    devicePixelHeight: number,
    viewWidth: number,
    viewHeight: number,
  ): OverlayFrame {
    const aspect = devicePixelWidth / Math.max(1, devicePixelHeight);
    const eye = this.camera.eye();
    const visible = this.wallNormals.map((wallNormal, wallIndex) => this.wallVisible(eye, wallIndex));
    const viewProjection = this.camera.viewProj(aspect);
    const grid = this.gridPlacement(viewProjection, viewWidth, viewHeight);
    const drawGrid = () => this.grid.draw(grid, devicePixelWidth / Math.max(1, viewWidth), devicePixelHeight);
    if (this.quality === 'high' && this.lit?.supported) {
      this.lit.render(viewProjection, this.camera.view(), this.camera.proj(aspect), visible, devicePixelWidth, devicePixelHeight, drawGrid);
    } else {
      this.renderer.render(viewProjection, this.camera.view(), visible, devicePixelWidth, devicePixelHeight, drawGrid);
    }
    this.gadgetLayout = null;
    if (this.gadget) {
      this.gadgetLayout = buildGadget(
        this.gadget,
        eye,
        this.camera.feetPerPixelAt(this.gadget.center, viewHeight),
        this.gadgetDrag?.handle ?? null,
        this.inputKind,
      );
      this.renderer.renderOverlay(this.gadgetLayout.mesh, viewProjection);
    }
    this.dirty = false;
    return {
      labels: this.labelPlacements(viewProjection, viewWidth, viewHeight),
      grid,
      angle: { azimuth: this.camera.azimuth, elevation: this.camera.elevation },
    };
  }

  /** The pointer's ray through a view-space point (draw's viewWidth/viewHeight units). */
  private viewRay(x: number, y: number, viewWidth: number, viewHeight: number): PickRay {
    return this.camera.ray((x / viewWidth) * 2 - 1, 1 - (y / viewHeight) * 2, viewWidth / viewHeight);
  }

  /** Under touch, a touch target's reach on screen in feet at each distance along `ray`; a mouse picks exactly. */
  private pickReach(ray: PickRay, viewHeight: number): PickReach | undefined {
    if (this.inputKind !== 'touch') return undefined;
    return distance => TOUCH_TARGET_REACH * this.camera.feetPerPixelAt(add(ray.origin, scale(ray.dir, distance)), viewHeight);
  }

  /** Resolve a view-space point (same units/origin as draw's viewWidth/viewHeight)
   *  to the nearest visible pickable, or null on empty space. */
  pick(x: number, y: number, viewWidth: number, viewHeight: number): S | null {
    if (viewWidth < 1 || viewHeight < 1) return null;
    const eye = this.camera.eye();
    const ray = this.viewRay(x, y, viewWidth, viewHeight);
    return pickAt(this.pickables, ray, groupIndex => this.wallVisible(eye, groupIndex), this.pickReach(ray, viewHeight));
  }

  /** The move gadget's handle under a view-space point, as drawn last frame; null off the gadget. */
  pickGadget(x: number, y: number, viewWidth: number, viewHeight: number): GadgetHandle | null {
    if (!this.gadgetLayout || viewWidth < 1 || viewHeight < 1) return null;
    return pickGadget(this.gadgetLayout, this.viewRay(x, y, viewWidth, viewHeight));
  }

  /**
   * Start dragging a gadget handle from a view-space point; the handle lights
   * until `endGadgetDrag`. A camera glide stops, since the drag reads the
   * camera's rays and the camera must hold still under it. False when the
   * drag cannot start.
   */
  beginGadgetDrag(handle: GadgetHandle, x: number, y: number, viewWidth: number, viewHeight: number): boolean {
    if (!this.gadget) return false;
    const drag = beginGadgetDrag(handle, this.gadget.center, this.viewRay(x, y, viewWidth, viewHeight));
    if (drag) this.camera.stopGlide();
    this.gadgetDrag = drag;
    this.dirty = true;
    return !!drag;
  }

  /** How far the gadget drag has come, in plan feet and height; null when no drag is on. */
  gadgetDragDelta(x: number, y: number, viewWidth: number, viewHeight: number): GadgetDelta | null {
    return this.gadgetDrag ? gadgetDragDelta(this.gadgetDrag, this.viewRay(x, y, viewWidth, viewHeight)) : null;
  }

  endGadgetDrag(): void {
    this.gadgetDrag = null;
    this.dirty = true;
  }

  /** Feet per CSS pixel where the gadget stands, as drawn last frame (a snap's reach on screen); null without a gadget. */
  gadgetFeetPerPixel(): number | null {
    return this.gadgetLayout?.feetPerPixel ?? null;
  }

  /** Release the GL context. Safe to call once at teardown. */
  dispose(): void {
    this.renderer.releaseContext();
  }
}
